import {
  type TextProps,
  TouchableOpacity,
  View,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
  Text,
  StyleSheet,
} from 'react-native'
import CloseIcon from '@/src/assets/icons/Close'
import { ChevronLeft } from '@/src/assets/icons/ChevronLeft'
import { type ComponentProps, type PropsWithChildren, type JSX, useMemo } from 'react'
import { mesaColorNames, typography } from '@/src/common/theme'
import { Link } from 'expo-router'

export function Section({
  title,
  onBackPress,
  titleStyles,
  titleTestID,
  chevronColor,
  onClosePress,
  styleOverride,
  style,
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  headerStyle,
  closeStyle,
  backProps,
  backLinkProps,
  right,
}: {
  title?: string
  onBackPress?: () => void
  onClosePress?: () => void
  titleStyles?: StyleProp<TextStyle>
  titleTestID?: TextProps['testID']
  chevronColor?: string
  /** bizarrely, the root `View`'s styling _isn't_ getting overwritten by the styles provided by the parent... */
  styleOverride?: StyleProp<ViewStyle>
  style?: StyleProp<ViewStyle>
  /** @deprecated just use {@link style} */
  headerStyle?: ViewStyle
  closeStyle?: ComponentProps<typeof TouchableOpacity>['style']
  backProps?: Omit<ComponentProps<typeof TouchableOpacity>, 'onPress'>
  backLinkProps?: ComponentProps<typeof Link>
  right?: (close?: JSX.Element) => PropsWithChildren['children']
}) {
  const rightContent = onClosePress && (
    <TouchableOpacity testID="close" onPress={onClosePress} style={[styles.closeIcon, closeStyle]}>
      <CloseIcon color={chevronColor ?? mesaColorNames['Mesa - Brand/Secondary #223843']} />
    </TouchableOpacity>
  )

  const backContent = useMemo(() => {
    if (onBackPress || backLinkProps) {
      const content = (
        <TouchableOpacity
          {...backProps}
          onPress={onBackPress}
          style={StyleSheet.flatten([styles.backIcon, backProps?.style])}
          testID="Section.Press.Back"
        >
          <ChevronLeft
            size={24}
            color={chevronColor ?? mesaColorNames['Mesa - Brand/Secondary #223843']}
          />
        </TouchableOpacity>
      )

      return backLinkProps ? (
        <Link asChild {...backLinkProps}>
          {content}
        </Link>
      ) : (
        content
      )
    }
  }, [onBackPress, backLinkProps, backProps, chevronColor])

  return (
    <View style={[styleOverride || styles.header, style, headerStyle]}>
      {backContent}
      {title ? (
        <Text
          style={[typography['heading/heading4'], styles.title, titleStyles]}
          testID={titleTestID}
        >
          {title}
        </Text>
      ) : null}
      {right?.(rightContent) || rightContent}
    </View>
  )
}

/** @deprecated just use the named export for {@link Section} */
export default Section

const styles = StyleSheet.create({
  header: {
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: 32,
    marginBottom: 24,
  },
  closeIcon: {
    flexBasis: 'auto',
    position: 'absolute',
    right: 16,
  },
  backIcon: {
    flexBasis: 'auto',
    position: 'absolute',
    left: 16,
  },
  title: {
    color: mesaColorNames['Mesa - Brand/Secondary #223843'],
    maxWidth: '82%',
    marginHorizontal: 'auto',
    textAlign: 'center',
  },
})
